import { type DeviceData,type IncidentRuleCommon } from '../devices/src/lib/models/devices';
import { type UserModel } from './users';

export enum IncidentColumn {
  COMMENTS = 'comments',
  INDEX = 'index',
  TICKET_NUMBER = 'ticketNumber',
  STATUS = 'status',
  INCIDENT_RULE = 'incidentRule',
  DEVICE_ID = 'deviceId',
  MANUFACTURER = 'manufacturer',
  MODEL = 'model',
  SERIAL_NUMBER = 'serialNumber',
  BUILDING = 'building',
  FLOOR = 'floor',
  ROOM = 'room',
  DEVICE_TYPE = 'deviceType',
  REPORTED = 'reported',
  RESOLVED = 'resolved',
}

export const incidentColumnTitle: Record<string, string> = {
  [IncidentColumn.STATUS]: 'Severity',
  [IncidentColumn.REPORTED]: 'Reported',
  [IncidentColumn.RESOLVED]: 'Resolved',
  [IncidentColumn.INCIDENT_RULE]: 'Incident Rule',
  [IncidentColumn.DEVICE_ID]: 'Device Name',
  [IncidentColumn.DEVICE_TYPE]: 'Device Type',
  [IncidentColumn.MANUFACTURER]: 'Manufacturer',
  [IncidentColumn.MODEL]: 'Model',
  [IncidentColumn.SERIAL_NUMBER]: 'Serial №',
  [IncidentColumn.BUILDING]: 'Building Name',
  [IncidentColumn.FLOOR]: 'Floor',
  [IncidentColumn.ROOM]: 'Room',
  [IncidentColumn.COMMENTS]: 'Comments',
};

export const incidentColumnOptions = Object.keys(incidentColumnTitle).map(key => ({
  title: incidentColumnTitle[key],
  value: key,
}));

export interface IncidentModel {
  assetId: string;
  clientId: string;
  createdTimestamp: string;
  description: string;
  device: DeviceData;
  fault: {
    description: string;
    name: string;
  };
  id: string;
  incidentNumber: number;
  incidentRule: Pick<IncidentRuleCommon, 'id' | 'name' | 'code' | 'operator' | 'conditionValue'> | null;
  inProgressSubStatus: string | null;
  lastUpdatedTimestamp: string;
  resolvedTimestamp: string | null;
  severity: IncidentSeverity;
  status: IncidentStatus;
}

export interface IncidentNoteModel {
  document: null | string;
  id: string;
  source: string;
  textValue: string;
  timeStamp: string;
  user: UserModel;
}

export enum IncidentStatus {
  IN_PROGRESS = 'inProgress',
  IN_QUEUE = 'new',
  RESOLVED = 'resolved',
}

export enum IncidentTabStatusFilter {
  OPEN = 'open',
  HISTORY = 'history',
}

export interface NormalizedIncident {
  assignedUser: string;
  building: string;
  buildingId: string;
  deviceId: string;
  deviceName: string;
  deviceType: string;
  deviceTypeId: string;
  floor: string;
  floorId: string;
  id: string;
  incidentRule: string;
  manufacturer: string;
  model: string;
  reported: string;
  resolved: string;
  room: string;
  roomId: string;
  serialNumber: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  ticketNumber: string;
}

export enum IncidentSeverity {
  MINOR = 'minor',
  MAJOR = 'major',
  CRITICAL = 'critical',
}

export const normalizeIncidents = (incidents: IncidentModel[]): NormalizedIncident[] =>
  incidents.map(incident => ({
    assignedUser: '',
    building: incident.device.location.friendlyName,
    buildingId: incident.device.location.id,
    deviceId: incident.device.id,
    deviceName: incident.device.friendlyName,
    deviceType: incident.device.deviceModelInformation?.deviceType || '',
    deviceTypeId: incident.device.deviceModelInformation?.type?.id || '',
    floor: incident.device.spacePath?.[0]?.friendlyName || '',
    floorId: incident.device.spacePath?.[0]?.id || '',
    id: incident.id,
    incidentRule: incident.incidentRule?.name
      || (incident.incidentRule
        ? `${incident.incidentRule.code} ${incident.incidentRule.operator} ${incident.incidentRule.conditionValue}`
        : ''),
    manufacturer: incident.device.deviceModelInformation?.make.name || '',
    model: incident.device.deviceModelInformation?.name || '',
    parentId: incident.device?.parentDevice?.id || '',
    parentName: incident.device?.parentDevice?.friendlyName || '',
    reported: new Date(incident.createdTimestamp).toLocaleString('en-US'),
    resolved: incident.resolvedTimestamp ? new Date(incident.resolvedTimestamp).toLocaleString('en-US') : '',
    room: incident.device.spacePath?.[1]?.friendlyName || '',
    roomId: incident.device.spacePath?.[1]?.id || '',
    serialNumber: incident.device.additionalAttributes?.find(attr => attr.name === 'serialNumber')?.value || '',
    severity: incident.severity,
    status: incident.status,
    ticketNumber: `${incident.incidentNumber}`,
  }));
